import RenderManager from '../rendering/render-manager';
import SlideManager from '../utils/slide-manager';
import LimitManager from '../interactivity/limit-manager';
import RotationSpace from '../utils/rotation-space';

const ROTATION_SENSITIVITY = 2;
const ROTATION_DAMPING_MULTIPLIER = 0.6;
const ROTATION_SPEED_THRESHOLD = 0.01;
const TRANSLATION_SENSITIVITY = 0.4;
const TRANSLATION_DAMPING_MULTIPLIER = 1 - TRANSLATION_SENSITIVITY;
const TRANSLATION_SPEED_THRESHOLD = 0.001;
const SCALE_SENSITIVITY = 0.001;
const SCALE_DAMPING_MULTIPLIER = 0.6;
const SCALE_SPEED_THRESHOLD = 1;
const AUTOSPIN_STRENGTH = 0.1;
const AUTOSPIN_THRESHOLD = 0.0001;

const oldMousePos = new THREE.Vector2();
const fovSize = new THREE.Vector2();
const deltaMousePos = new THREE.Vector2();
const addAngVel = new THREE.Vector3();
const axis = new THREE.Vector3();
const quaternion = new THREE.Quaternion();
const addVel = new THREE.Vector3();
const velocity = new THREE.Vector3();
const relPos0 = new THREE.Vector3();
const relPos1 = new THREE.Vector3();
const pos1 = new THREE.Vector3();

const tmpMatrix1 = new THREE.Matrix4();
const tmpMatrix2 = new THREE.Matrix4();

const ValidDragActions = new Set(["rotate", "translate"]);

const RotationModes = new Set(['orbit', 'trackball', 'horizontal']);

class ObjectRotationControls {
    constructor(camera) {
        this._camera = camera;

        this._events = new THREE.EventDispatcher();

        this._selectObject = null;
        this._getObjectDefaultRotation = null;

        this._enabled = false;
        this._target = null;

        this._dragging = null;
        this._objectTranslations = null;
        this._objectRotations = null;
        this._objectScales = null;
        this._objectRotationSpaces = null;

        this._autoSpin = new Map();

        this._rotationMode = 'orbit';

        this._action = null;
    }

    get events() {
        return this._events;
    }

    set enabled(v) {
        v = Boolean(v);

        if (v === this._enabled) return;

        this._enabled = v;

        if (v) {
            this._target = new THREE.Vector3();
            this._camera.getWorldDirection(this._target);
            this._target.normalize();

            this._objectTranslations = new Map();
            this._objectRotations = new Map();
            this._objectScales = new Map();
            this._objectRotationSpaces = new Map();
        } else {
            this._dragging = null;

            this._objectTranslations = null;
            this._objectRotations = null;
            this._objectScales = null;
            this._objectRotationSpaces = null;
        }
    }

    get enabled() { return this._enabled; }

    get rotationMode() { return this._rotationMode; }
    set rotationMode(v) {
        if (!RotationModes.has(v)) {
            throw new Error('invalid rotation mode: ' + v);
        }

        this._rotationMode = v;
    }

    get object() { return this._camera; }

    setObjectSelector(selectObject) {
        this._selectObject = selectObject;
    }

    setDefaultRotationGetter(getObjectDefaultRotation) {
        this._getObjectDefaultRotation = getObjectDefaultRotation;
    }

    startDragging(action, mousePos) {
        if (!ValidDragActions.has(action)) {
            throw new Error("Invalid action: "+String(action));
        }

        if (this._dragging) {
            this.stopDragging();
        }

        const object = this._selectObject(mousePos);

        if (object) {
            const objectUuid = object.uuid;
            const animData = SlideManager.currentSlide.animation[objectUuid];
            if (animData && animData.animation && animData.animation.isPreset) {
                const action = Q3.mixer.getObjectAction(objectUuid).activeAction;

                if (action && action.enabled)
                    this._action = { action, paused: action.paused };
            }

            this._dragging = {
                object,
                action,
                oldMousePos: mousePos,
                startedDragging: false,
            };

            this._events.dispatchEvent({
                type: 'grab',
                action,
                object,
            });
        }

    }

    showSnackBar(message) {
        clearTimeout(this.snackTimeout);
        this.snackTimeout = setTimeout(() => Q3.showSnackBar(message), 200);
    }

    stopDragging() {
        if (!this._dragging) return;

        const { object, action, startedDragging } = this._dragging;
        const finalDragState = this._dragging;

        this._dragging = null;

        if (this._action) {
            const { action, paused } = this._action;

            this._action = null;
            action.paused = paused;
        }

        if (startedDragging) {
            this._events.dispatchEvent({
                type: 'dragstop',
                action,
                object,
            });
        }

        this._events.dispatchEvent({
            type: 'release',
            action,
            object,
        });

    }

    drag(newMousePos) {
        if (!this._dragging) return;

        const { action } = this._dragging;

        oldMousePos.copy(this._dragging.oldMousePos);

        this._dragging.oldMousePos.copy(newMousePos);
        deltaMousePos.copy(newMousePos).sub(oldMousePos);

        const activeSlide = SlideManager.activeSlide;
        const objectData = SlideManager.getSlide(activeSlide).animation[this._dragging.object.uuid];
        const cameraLimits = LimitManager.getObjectCameraLimits(activeSlide, this._dragging.object.uuid);
        const rotationMode = "objectRotationMode" in objectData ? objectData.objectRotationMode : this._rotationMode;

        switch (action) {
            case 'rotate':
                if (SlideManager.currentSlide.slideQuiz && SlideManager.currentSlide.slideQuiz.type === "plopQuiz")
                    return;

                let { fov, aspect } = this._camera;

                const rotData = this._getObjectRotationData(this._dragging.object);
                const fovSizeY = Math.tan(THREE.Math.degToRad(fov) * 0.5);
                const fovSizeX = fovSizeY * aspect;
                const camInvQuat = new THREE.Quaternion();

                for (let obj = this._camera; obj; obj = obj.parent)
                    camInvQuat.premultiply(obj.quaternion);

                camInvQuat.inverse();

                const relFrontAxis = new THREE.Vector3(0, 0, -1);
                const relUpAxis = rotData.upAxis.clone().applyQuaternion(camInvQuat);
                const relSideAxis = new THREE.Vector3().crossVectors(relFrontAxis, relUpAxis).normalize();

                if (relSideAxis.lengthSq() < 0.5)
                    relSideAxis.set(0, 1, 0).cross(relUpAxis).normalize();

                relUpAxis.crossVectors(relSideAxis, relFrontAxis).normalize();
                const deltaH = deltaMousePos.dot(relSideAxis) * fovSizeX;
                const deltaV = deltaMousePos.dot(relUpAxis) * fovSizeY;

                if (rotationMode !== 'horizontal')
                    rotData.angVel.x -= deltaV * ROTATION_SENSITIVITY;

                rotData.angVel.y += deltaH * ROTATION_SENSITIVITY;
                break;
            case 'translate':
                if (!cameraLimits || !cameraLimits.panLimitEnabled) {
                    const objPos = this._dragging.object.position;
                    const parent = this._dragging.object.parent;
                    const parentMat = parent ? parent.matrixWorld : tmpMatrix1.identity();
                    const parentInv = tmpMatrix2.getInverse(parentMat);

                    relPos0.copy(objPos).applyMatrix4(parentMat).applyMatrix4(this._camera.matrixWorldInverse).applyMatrix4(this._camera.projectionMatrix);
                    relPos1.copy(relPos0);

                    relPos1.x += deltaMousePos.x;
                    relPos1.y += deltaMousePos.y;

                    pos1.copy(relPos1).applyMatrix4(this._camera.projectionMatrixInverse).applyMatrix4(this._camera.matrixWorld).applyMatrix4(parentInv);

                    addVel.subVectors(pos1, objPos);
                    addVel.multiplyScalar(TRANSLATION_SENSITIVITY);

                    const trData = this._getObjectTranslationData(this._dragging.object);
                    trData.velocity.add(addVel);
                } else if (Q3.editMode)
                    this.showSnackBar("Panning is disabled");
                break;
        }

        if (!this._dragging.startedDragging) {
            this._dragging.startedDragging = true;

            this._events.dispatchEvent({
                type: 'dragstart',
                action,
                object: this._dragging.object,
            });
        }

        this._events.dispatchEvent({
            type: 'drag',
            action,
            object: this._dragging.object,
        });
    }

    zoom(mousePos, delta) {
        const object = this._selectObject(mousePos);

        if (object) {
            const scData = this._getObjectScaleData(object);
            scData.speed += delta;

            this._events.dispatchEvent({
                type: 'execute',
                action: 'scale',
                object,
            });
        }
    }

    update() {
        if (this._enabled) {
            let draggingObject = this._dragging ? this._dragging.object : null;
            for (let [object, autoSpin] of this._autoSpin) {
                let { enabled, targetSpeed, currentSpeed, axis } = autoSpin;

                // axis.copy(THREE.Object3D.DefaultUp).applyQuaternion(startQuaternion.clone().inverse());

                if (object === draggingObject) {
                    targetSpeed = 0;
                }

                let addSpeed = (targetSpeed - currentSpeed) * AUTOSPIN_STRENGTH;
                currentSpeed += addSpeed;

                autoSpin.currentSpeed = currentSpeed;

                // const inverse = new THREE.Matrix4().getInverse(object.parent.matrixWorld);
                // const rotMat = new THREE.Matrix4()
                //     .makeRotationFromQuaternion(quaternion.setFromAxisAngle(axis, currentSpeed));

                quaternion
                    .setFromAxisAngle(axis, currentSpeed)
                // .applyMatrix4();
                object.quaternion.premultiply(quaternion);

                RenderManager.activeCamera.update = true;

                if (!enabled && currentSpeed <= AUTOSPIN_THRESHOLD) {
                    this._autoSpin.delete(object);
                }
            }

            const currentSlide = SlideManager.currentSlide;

            for (let [object, rotData] of this._objectRotations) {
                const { rotationSpace, angVel } = rotData;

                const worldParentQuat = new THREE.Quaternion();
                for (let obj = object.parent; obj; obj = obj.parent) {
                    worldParentQuat.premultiply(obj.quaternion);
                }

                {
                    const worldObjQuat = worldParentQuat.clone().multiply(object.quaternion);

                    const worldCamQuat = new THREE.Quaternion();
                    for (let obj = this._camera; obj; obj = obj.parent) {
                        worldCamQuat.premultiply(obj.quaternion);
                    }

                    rotationSpace.objCamQuatChanged(worldObjQuat, worldCamQuat);
                }

                rotationSpace.angleH -= angVel.y;
                rotationSpace.angleV -= angVel.x;

                angVel.x *= ROTATION_DAMPING_MULTIPLIER;
                angVel.y *= ROTATION_DAMPING_MULTIPLIER;

                const worldCamQuat = new THREE.Quaternion().setFromRotationMatrix(this._camera.matrixWorld);
                const relObjQuat = rotationSpace.getObjQuatWithRespectToCam();
                const worldObjQuat = worldCamQuat.clone().multiply(relObjQuat);
                const worldObjParentInvQuat = worldParentQuat.clone().inverse();

                object.quaternion.copy(worldObjQuat).premultiply(worldObjParentInvQuat);

                if (Math.max(Math.abs(angVel.x), Math.abs(angVel.y)) <= ROTATION_SPEED_THRESHOLD) {
                    this._objectRotations.delete(object);
                }

                this._events.dispatchEvent({
                    type: 'change',
                    action: 'rotate',
                    object,
                });
            }

            for (let [object, trData] of this._objectTranslations) {
                const vel = trData.velocity;

                velocity.copy(vel);
                object.position.add(velocity);
                vel.multiplyScalar(TRANSLATION_DAMPING_MULTIPLIER);

                if (vel.lengthSq() <= TRANSLATION_SPEED_THRESHOLD * TRANSLATION_SPEED_THRESHOLD) {
                    this._objectTranslations.delete(object);
                }

                this._events.dispatchEvent({
                    type: 'change',
                    action: 'translate',
                    object,
                });
            }

            for (let [object, scData] of this._objectScales) {
                let speed = scData.speed;

                const multiplier = Math.pow(2, speed * SCALE_SENSITIVITY);
                speed *= SCALE_DAMPING_MULTIPLIER;
                scData.speed = speed;
                object.scale.multiplyScalar(multiplier);

                if (Math.abs(speed) <= SCALE_SPEED_THRESHOLD)
                    this._objectScales.delete(object);

                this._events.dispatchEvent({
                    type: 'change',
                    action: 'scale',
                    object,
                });
            }
        }

    }

    _getObjectRotationData(object) {
        let rotData = this._objectRotations.get(object);
        const objectData = SlideManager.currentSlide.animation[object.uuid];
        const rotationMode = "objectRotationMode" in objectData ? objectData.objectRotationMode : this._rotationMode;

        if (rotData && rotData.mode !== rotationMode) {
            this._objectRotations.delete(object);
        }

        let hasRotData = this._objectRotations.has(object);
        let hasRotSpace = this._objectRotationSpaces.has(object);

        if (hasRotData) {
            rotData = this._objectRotations.get(object);

            if (hasRotSpace) {
                return rotData;
            }
        } else {
            rotData = {
                mode: rotationMode,
                angVel: new THREE.Euler(),
            };
            this._objectRotations.set(object, rotData);
        }

        if (!hasRotSpace) {
            const camQuat = new THREE.Quaternion();
            for (let obj = this._camera; obj; obj = obj.parent) {
                camQuat.premultiply(obj.quaternion);
            }

            const objQuat = new THREE.Quaternion();
            for (let obj = object; obj; obj = obj.parent) {
                objQuat.premultiply(obj.quaternion);
            }

            const upAxis = new THREE.Vector3(0, 1, 0);

            {
                const defaultInvQuat = this._getObjectDefaultRotation(object).inverse();
                upAxis.applyQuaternion(defaultInvQuat);
                upAxis.applyQuaternion(objQuat);
            }

            const rotSpace = new RotationSpace(camQuat, objQuat, upAxis);
            this._objectRotationSpaces.set(object, rotSpace);
            rotData.rotationSpace = rotSpace;
            rotData.upAxis = upAxis;
        } else {
            if (!hasRotData) {
                const rotSpace = this._objectRotationSpaces.get(object);
                rotData.rotationSpace = rotSpace;
                rotData.upAxis = new THREE.Vector3(0, 1, 0).applyQuaternion(rotSpace.spaceQuaternion);
            }
        }

        return rotData;
    }

    _getObjectTranslationData(object) {
        if (!this._objectTranslations.has(object)) {
            this._objectTranslations
                .set(object, { velocity: new THREE.Vector3() });
        }

        return this._objectTranslations.get(object);
    }

    _getObjectScaleData(object) {
        if (!this._objectScales.has(object)) {
            this._objectScales
                .set(object, { speed: 0 });
        }

        return this._objectScales.get(object);
    }

    clearObjectRotationSpaces() {
        if (this._objectRotationSpaces) {
            for (let [object, rotSpace] of this._objectRotationSpaces) {
                let rotData = this._objectRotations.get(object);
                if (rotData) {
                    rotData.rotationSpace = null;
                    rotData.upAxis = null;
                }
            }

            this._objectRotationSpaces.clear();
        }
    }


    setAutoSpin(object, enabled, speed) {
        const autoSpin = this._autoSpin.get(object);

        if (!autoSpin) {
            if (enabled) {
                const rot = SlideManager.currentSlide.animation[object.uuid].rotation;
                const eul = new THREE.Euler(rot.x, rot.y, rot.z);
                const quat = new THREE.Quaternion().setFromEuler(eul);
                const axis = new THREE.Vector3(0, 0, 1).applyQuaternion(quat);

                this._autoSpin.set(object, {
                    enabled,
                    targetSpeed: speed,
                    currentSpeed: 0,
                    axis
                });
            } else return;
        } else {
            autoSpin.enabled = enabled;
            autoSpin.targetSpeed = enabled ? speed : 0;
            this._autoSpin.delete(object);
        }
    }
}

export default ObjectRotationControls;
export { ObjectRotationControls };
